"use client";

import { useState, type FormEvent } from "react";
import { PROJECT_TYPES, SITE } from "@/lib/site";

// The estimate form posts to the public contact API (API Gateway → Lambda →
// DynamoDB + best-effort SMS). The URL is inlined at build time from
// NEXT_PUBLIC_CONTACT_API_URL (set by deploy.yml from a Terraform output).
const CONTACT_API = process.env.NEXT_PUBLIC_CONTACT_API_URL;

type Status = "idle" | "sending" | "ok" | "error";

export default function ContactForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);

    // Honeypot — bots fill hidden fields; humans don't. Pretend success.
    if (data.get("website")) {
      setStatus("ok");
      form.reset();
      return;
    }

    const payload = {
      name: String(data.get("name") || "").trim(),
      phone: String(data.get("phone") || "").trim(),
      email: String(data.get("email") || "").trim(),
      projectType: String(data.get("projectType") || "").trim(),
      message: String(data.get("message") || "").trim(),
    };

    setStatus("sending");
    setError(null);
    try {
      if (!CONTACT_API) {
        throw new Error("The contact form isn't connected yet — please call us instead.");
      }
      const res = await fetch(`${CONTACT_API.replace(/\/$/, "")}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(
          body?.error ||
            "Sorry — something went wrong sending your message. Please try again or call us.",
        );
      }
      setStatus("ok");
      form.reset();
    } catch (err) {
      setStatus("error");
      setError(err instanceof Error ? err.message : "Something went wrong.");
    }
  }

  if (status === "ok") {
    return (
      <div className="bg-cream px-9 py-12 text-center text-navy">
        <p className="eyebrow-plain mb-4">Request Received</p>
        <h2 className="font-serif text-3xl font-semibold text-navy">
          Thank you — we&apos;ll be in touch.
        </h2>
        <p className="mx-auto mt-4 max-w-sm text-[15px] leading-[1.7] text-taupe">
          We&apos;ll follow up shortly to schedule your no-obligation, on-site
          estimate. For immediate help, call or text{" "}
          <a href={SITE.phoneHref} className="font-semibold text-gold-dark">
            {SITE.phone}
          </a>
          .
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="bg-cream px-8 py-9 text-navy sm:px-9">
      <div className="mb-4 grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="name" className="form-label">
            Name
          </label>
          <input id="name" name="name" type="text" required className="form-input" />
        </div>
        <div>
          <label htmlFor="phone" className="form-label">
            Phone
          </label>
          <input id="phone" name="phone" type="tel" required className="form-input" />
        </div>
      </div>

      <div className="mb-4">
        <label htmlFor="email" className="form-label">
          Email
        </label>
        <input id="email" name="email" type="email" required className="form-input" />
      </div>

      <div className="mb-4">
        <label htmlFor="projectType" className="form-label">
          Project Type
        </label>
        <select id="projectType" name="projectType" className="form-input" defaultValue="">
          <option value="" disabled>
            Select an option
          </option>
          {PROJECT_TYPES.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </div>

      <div className="mb-[22px]">
        <label htmlFor="message" className="form-label">
          Project Details
        </label>
        <textarea
          id="message"
          name="message"
          rows={3}
          required
          className="form-input resize-none"
        />
      </div>

      {/* Honeypot — visually hidden, off the tab order. */}
      <div aria-hidden className="hidden">
        <label>
          Leave this field empty
          <input type="text" name="website" tabIndex={-1} autoComplete="off" />
        </label>
      </div>

      {status === "error" && error && (
        <p role="alert" className="mb-4 text-sm font-medium text-red-700">
          {error}
        </p>
      )}

      <button type="submit" disabled={status === "sending"} className="btn-primary w-full">
        {status === "sending" ? "Sending…" : "Request My Free Estimate"}
      </button>
    </form>
  );
}
